import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { plainToClass } from 'class-transformer';
import {
  AppPercentPipe, Property,
  PropertyCorelogicStatsInterface,
  PropertyService,
  PropertyValuation,
  PropertyValuationForm,
  PropertyValuationMessages,
  PropertyValuationService
} from 'taxtank-core';
import { BaseFormComponent } from '@shared/component/base/base-form.component';
import { CustomCurrencyPipe } from '@shared/pipe/custom-currency/custom-currency.pipe';

/**
 * Property valuation form component. Contain property valuation own fields
 */
@Component({
  selector: 'app-property-valuation-form',
  templateUrl: './property-valuation-form.component.html',
  styleUrls: ['./property-valuation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PropertyValuationFormComponent extends BaseFormComponent implements OnInit {
  isCorelogic: boolean;
  isCorelogicAvailable: boolean;
  corelogicStats: PropertyCorelogicStatsInterface;
  // PropertyValuation instance new or existing
  @Input() valuation: PropertyValuation = plainToClass(PropertyValuation, {});
  @Input() property: Property;
  @Input() isSwitcherHidden = false;
  // form with property valuation fields
  @Input() form: PropertyValuationForm;
  // Max value by which the growth percentage can deviate
  percentageRange = 3;
  growthPercentMin = 0;
  growthPercentMax = 10;
  growthPercentMid = 5;
  // Step by which the growth percentage can change
  percentageStep = 0.1;
  isManualMV = false;

  private propertyValuationService = inject(PropertyValuationService);
  private propertyService = inject(PropertyService);
  public customCurrencyPipe = inject(CustomCurrencyPipe);
  public percentPipe = inject(AppPercentPipe);

  currencyTransformer = this.customCurrencyPipe.transform.bind(this.customCurrencyPipe);
  percentTransformer = this.percentPipe.transform.bind(this.percentPipe);

  ngOnInit(): void {
    this.valuation.property = this.property;
    this.isCorelogic = this.isCorelogicAvailable = !this.valuation.id && this.property.isCorelogicAvailable();

    if (!this.form) {
      this.buildForm();
    }

    if (this.isCorelogic) {
      this.propertyService.getCorelogicStats(this.property).subscribe(corelogicStats => {
        this.corelogicStats = corelogicStats;
        if (corelogicStats.growthPercent) {
          this.growthPercentMin = corelogicStats.growthPercent - this.percentageRange;
          this.growthPercentMax = corelogicStats.growthPercent + this.percentageRange;
          this.growthPercentMid = (this.growthPercentMin + this.growthPercentMax) / 2;
        }
        this.isManualMV = !corelogicStats.marketValue;

        this.valuation.growthPercent = this.corelogicStats.growthPercent ?? 5;
        this.valuation.marketValue = this.corelogicStats.marketValue;
        this.valuation.lowMarketValue = this.corelogicStats.lowMarketValue;
        this.valuation.highMarketValue = this.corelogicStats.highMarketValue;
        this.valuation.confidence = this.corelogicStats.confidence;

        this.buildForm();
      });
    }
  }

  buildForm(): void {
    this.form = new PropertyValuationForm(this.valuation, this.property);
  }

  save(valuation: PropertyValuation): void {
    valuation.id ? this.put(valuation) : this.post(valuation);
  }

  post(valuation: PropertyValuation): void {
    this.propertyValuationService.post(valuation).subscribe(() => {
      this.saved.emit(valuation);
      this.toastService.success(PropertyValuationMessages.CREATED);
    });
  }

  put(valuation: PropertyValuation): void {
    this.propertyValuationService.put(valuation).subscribe(() => {
      this.saved.emit(valuation);
      this.toastService.success(PropertyValuationMessages.UPDATED);
    });
  }
}
